import type { PlanId } from 'src/types/plan/plan-domain';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { toLookbackDays } from 'src/utils/plan/plan-lookback';
import { researchQuotaLabel } from 'src/utils/plan/plan-research';
import {
  planEntryOf,
  planMonthlyWon,
  publicPlanName,
  publicPlanBadge,
  PUBLIC_PLAN_IDS,
  PLAN_MENU_FIELDS,
  PUBLIC_PLAN_BADGE_LABEL,
} from 'src/utils/plan/plan-public-ladder';

import { RADIUS } from 'src/theme/ui-tokens';
import { PlanService } from 'src/constants/plan-service';
import { periodLabelOf } from 'src/constants/plan-period';

import { trackPublicCta } from 'src/sections/common/track-public-cta';

import { CM, SECTION_PX, SECTION_PY, PUBLIC_TEXT, keepPhrases, PUBLIC_CTA_SX, PUBLIC_MAX_WIDTH } from '../constants/home-theme';

// ----------------------------------------------------------------------

/**
 * 요금 맛보기 — 브랜드 성장 단계 다섯(LITE → INDIVIDUAL → GROWTH → BRAND → ENTERPRISE). 자세한 값은 요금 화면이 말한다.
 * 🔑 플랜 이름·값·조회 기간·Research 횟수·그 단계에서 새로 열리는 메뉴는 전부 플랜 표에서 읽는다 — 여기 숫자를 적지 않는다.
 *   여기 적는 것은 단계 이름(`STAGE`)뿐이다 — 메뉴의 화면 이름은 `PLAN_MENU_FIELDS` 가 쥔다.
 * ⚠️ ENTERPRISE 의 한도는 「커스텀」이다(무제한이라고 쓰지 않는다).
 */

/** 플랜마다 붙는 단계 이름 — 카드 제목 */
const STAGE: Record<PlanId, string> = {
  plan_lite: '레퍼런스를 찾기 시작할 때',
  plan_individual: '리서치가 매일 일이 될 때',
  plan_growth: '우리 브랜드 반응을 챙길 때',
  plan_brand: '경쟁사와 시딩까지 관리할 때',
  plan_enterprise: '한도와 연동을 맞춰야 할 때',
} as Record<PlanId, string>;

const won = (value: number) => value.toLocaleString('ko-KR');

/** 카드 한 장의 값 — 전부 플랜 표에서 */
function stepOf(planId: PlanId) {
  const days = toLookbackDays(PlanService[planId]?.search?.searchDays);
  return {
    planId,
    name: publicPlanName(planId),
    stage: STAGE[planId] ?? '',
    menus: PLAN_MENU_FIELDS.filter(({ field }) => planEntryOf(field) === planId).flatMap(({ menus }) => menus.map((menu) => menu.label)),
    limits: [days > 0 ? `조회 기간 ${periodLabelOf(days)}` : '', `Research ${researchQuotaLabel(planId)}`].filter(Boolean),
    price: planMonthlyWon(planId),
    /** 「신규」만 작게 단다 — 「추천」은 요금 화면의 몫이라 홈 카드에는 안 단다 */
    isNew: publicPlanBadge(planId) === 'new',
  };
}

export function HomePlansSection() {
  const steps = PUBLIC_PLAN_IDS.filter((planId) => planId !== 'plan_enterprise').map(stepOf);
  const nameOf = (field: string) => {
    const planId = planEntryOf(field);
    return planId ? publicPlanName(planId) : '';
  };

  return (
    <Box id="plans" data-section-id="plans" component="section" sx={{ px: SECTION_PX, py: SECTION_PY, bgcolor: CM.bgSection }}>
      <Box sx={{ maxWidth: PUBLIC_MAX_WIDTH, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography sx={{ ...PUBLIC_TEXT.eyebrow, color: CM.primary, mb: 1.5 }}>요금</Typography>
          <Typography component="h2" sx={{ ...PUBLIC_TEXT.title, color: CM.textDark, mb: 2 }}>
            브랜드 성장 단계에 맞춘 요금제
          </Typography>
          <Typography sx={{ ...PUBLIC_TEXT.lead, color: CM.textMuted, maxWidth: 680, mx: 'auto' }}>
            {keepPhrases(`레퍼런스 찾기 ${nameOf('searchYN')} · 브랜드 반응 ${nameOf('brandYN')} · 시딩 ${nameOf('campaignYN')}`, 16)}
          </Typography>
        </Box>

        {/* 넓은 폭은 넷 + ENTERPRISE 가로 띠, 태블릿은 둘씩, 폰은 한 줄씩 — 가격 줄은 카드 바닥에 붙여 높이를 맞춘다 */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
          {steps.map((step) => (
            <Stack key={step.planId} sx={{ p: 3, borderRadius: RADIUS.lg, border: `1px solid ${CM.border}`, bgcolor: '#fff' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography sx={{ ...PUBLIC_TEXT.eyebrow, color: CM.primary }}>{step.name}</Typography>
                {step.isNew && (
                  <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.6, px: 0.75, borderRadius: RADIUS.sm, color: CM.primary, bgcolor: CM.alpha(0.1) }}>
                    {PUBLIC_PLAN_BADGE_LABEL.new}
                  </Typography>
                )}
              </Stack>
              <Typography component="h3" sx={{ fontSize: 17, fontWeight: 600, textWrap: 'balance', color: CM.textDark, mb: 1 }}>{step.stage}</Typography>
              <Typography sx={{ fontSize: 14, lineHeight: 1.6, textWrap: 'balance', color: CM.textMuted, mb: 1 }}>{keepPhrases(step.menus.join(' · '))}</Typography>
              <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: CM.textSubtle, mb: 2 }}>{keepPhrases(step.limits.join(' · '), 16)}</Typography>
              {step.price !== null && (
                <Typography sx={{ mt: 'auto', fontSize: 14, color: CM.textBody }}>
                  월 <Box component="span" sx={{ fontWeight: 600, color: CM.textDark }}>{won(step.price)}원</Box>
                </Typography>
              )}
            </Stack>
          ))}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            columnGap={3}
            sx={{ gridColumn: '1 / -1', p: 3, borderRadius: RADIUS.lg, bgcolor: CM.textDark }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Typography sx={{ ...PUBLIC_TEXT.eyebrow, color: CM.primaryLight, mb: 1 }}>{publicPlanName('plan_enterprise')}</Typography>
              <Typography component="h3" sx={{ fontSize: 17, fontWeight: 600, textWrap: 'balance', color: '#fff', mb: { xs: 1, md: 0 } }}>{STAGE.plan_enterprise}</Typography>
            </Box>
            <Typography sx={{ flex: 1, fontSize: 14, lineHeight: 1.6, textWrap: 'balance', color: 'rgba(255,255,255,0.72)', mb: { xs: 2, md: 0 } }}>
              등록 개수·조회 기간 조정 · API 연동 · 새 데이터 소스
            </Typography>
            <Typography sx={{ flexShrink: 0, fontSize: 14, fontWeight: 600, color: '#fff' }}>별도 문의</Typography>
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="center" spacing={2}>
          <Button
            component={RouterLink}
            href={paths.subscription.plan}
            variant="outlined"
            onClick={() => trackPublicCta('home.plans.pricing', '요금제 자세히 보기')}
            sx={{ ...PUBLIC_CTA_SX, color: CM.text, borderColor: CM.border, bgcolor: '#fff', '&:hover': { borderColor: CM.primary, bgcolor: '#fff' } }}
          >
            요금제 자세히 보기
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
